import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { height, background, fontSize, fontWeight, width, space } from '../../util/styles'
import Copy from '../Copy'
import { themeColor } from '../../util/themeLenses'
import { injectIntl } from 'react-intl'
import messages from './messages'
const CodeInputContainer = styled.fieldset`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0px;
`
const DefaultInputComponent = styled.input`
    -webkit-appearance: none;
    -moz-appearance: textfield;
    -webkit-user-select: none;
    user-select: none;
    text-align: center;
    color: ${themeColor('gray')};
    background: ${themeColor('lightGray20')};
    border: 1px solid ${themeColor('main20')};
    border-color: ${({error}) => error ? themeColor('error') : 'transparent' };
    outline: none;
    position: relative;
    padding: 0;
    ${fontSize}
    ${fontWeight}
    ${background}
    ${height}
    ${width}
    &:focus {
        border-color: ${({error}) => error ? themeColor('error') : themeColor('main') };
        background: ${themeColor('main20')};
        color: ${themeColor('main')};
    }
    -webkit-outer-spin-button {
        -webkit-appearance: none;
    }
    -webkit-inner-spin-button {
        -webkit-appearance: none;
    }
    &::placeholder {
        ${fontSize}
        ${fontWeight}
        color: rgba(0,0,0,0.3);
    }
`

const InputContainer = styled.div`
    text-align: center;
    display: inline-block;
    ${space}
    &:last-child {
        margin-right: 0;
    }
`

const CodeInput = ({
    size,
    type,
    autoFocus,
    onChange,
    groupBy,
    separator,
    spaceBetweenInput,
    spaceBetweenGroup,
    fontSize,
    width,
    dataTest,
    height,
    inputComponent: InputComponent,
    error,
    value,
    intl,
    ...restProps
}) => {
    const [values, setValues] = useState(Array(size).fill(''))
    const inputRefs = useRef([])

    const EMPTY_VALUE = ''

    useEffect(() => {
        onChange(values.join('').trim())
    }, [values, onChange])

    useEffect(() => {
        value && setValues([...value])
    }, [value, setValues])

    const onKeyHandler = (index, refs) => (event) => {
        const targetValues = event.target.value.trim()

        if (targetValues.length === size) {
            event.stopPropagation()
            event.preventDefault()
            const newValues = Array(size).fill('')
            const tempValues = [...targetValues]
            tempValues.forEach((val, index) => { newValues[index] = val })
            setValues(newValues)
            const lastItem = Math.min(size, newValues.join('').trim().length) - 1
            inputRefs.current[lastItem].focus()
        } else {
            const value = targetValues[targetValues.length - 1]
            const onDelete = !value
            const newValues = [...values]
            const item = refs[index]
            const next = index < refs.length - 1 ? refs[index + 1] : null
            const prev = index > 0 ? refs[index - 1] : null
            newValues[index] = value || ''

            if (onDelete && prev) {
                newValues[index] = EMPTY_VALUE
                item.focus()
            } else if (value && next) {
                next.focus()
            } else {
                item.focus()
            }
            setValues(newValues)
            event.stopPropagation()
            event.preventDefault()
        }

        return false
    }

    return <CodeInputContainer onPaste={(event) => {
        event.stopPropagation()
        event.preventDefault()
        const text = event.clipboardData.getData('Text').replace(/\s+/g, '').substr(0, size).split('')
        const newValues = Array(size).fill('')
        text.forEach((val, index) => { newValues[index] = val })
        setValues(newValues)
        const lastItem = Math.min(size, newValues.join('').trim().length) - 1
        inputRefs.current[lastItem].focus()
    }}
    >{values.map((value, index) => {
        const showGroup = (index !== (values.length - 1)) &&
            (((index + 1) % groupBy) === 0)
        return <InputContainer key={`code_input_${index}`} marginRight={showGroup ? spaceBetweenGroup : spaceBetweenInput}>
            <InputComponent
                aria-label={intl.formatMessage(messages.codeInputAriaLabel, {
                    index: index + 1,
                    size: size,
                })}
                aria-invalid={error}
                dataTest={`${dataTest}[${index}]`}
                tabIndex={index + 1}
                autoComplete={index == 0 ? 'one-time-code' : 'off'}
                value={value}
                autoFocus={(index === 0) && autoFocus}
                type={type === 'number' ? 'text' : type}
                inputMode={type === 'number' ? 'numeric' : null}
                autoCorrect='off'
                autocapitalize='off'
                error={error}
                onContextMenu={(event) => {
                    event.stopPropagation()
                    event.preventDefault()
                    return false
                }}
                ref={el => (inputRefs.current[index] = el)}
                width={width}
                fontSize={fontSize}
                fontWeight={fontWeight}
                height={height}
                onSelect={(event) => {
                    event.stopPropagation()
                    event.preventDefault()
                    return false
                }}
                onFocus={(event) => {
                    inputRefs.current[index].value = EMPTY_VALUE
                    event.stopPropagation()
                    event.preventDefault()
                    return false
                }}
                placeholder={value}
                onBlur={(event) => {
                    inputRefs.current[index].value = value
                    event.stopPropagation()
                    event.preventDefault()
                    return false
                }}
                onChange={onKeyHandler(index, inputRefs.current)}
                onClick={(event) => {
                    event.stopPropagation()
                    event.preventDefault()
                    inputRefs.current[index].value = EMPTY_VALUE
                    return false
                }}
                onKeyPress={() => {
                    return false
                }}
                {...restProps} />
            {showGroup && separator &&
                <Copy display='inline-block' as='span' marginLeft={spaceBetweenInput} marginRight={spaceBetweenInput} padding='0' textAlign='center' fontSize={fontSize} weight={fontWeight} >
                    {separator}
                </Copy>
            }
        </InputContainer>
    })}</CodeInputContainer>
}

CodeInput.propTypes = {
    onChange: PropTypes.func,
    size: PropTypes.number,
    dataTest: PropTypes.string,
    groupBy: PropTypes.number,
    separator: PropTypes.string,
    inputComponent: PropTypes.any,
    spaceBetweenInput: PropTypes.any,
    spaceBetweenGroup: PropTypes.any,
    fontSize: PropTypes.any,
    width: PropTypes.any,
    height: PropTypes.any,
    type: PropTypes.oneOf(['password', 'text', 'number']),
    error: PropTypes.bool,
    value: PropTypes.string,
    autoFocus: PropTypes.bool,
    intl: PropTypes.object,
}

CodeInput.defaultProps = {
    onChange: () => { },
    groupBy: 3,
    size: 6,
    autoFocus: false,
    dataTest: 'code',
    fontSize: 'large',
    fontWeight: 'large',
    type: 'password',
    inputComponent: DefaultInputComponent,
    separator: '',
    height: '3em',
    width: ['40px', '2em'],
    spaceBetweenInput: '0.5em',
    spaceBetweenGroup: '2em',
    error: false,
    value: ''
}

export default injectIntl(CodeInput)
